import { useLayout } from 'hooks/useLayout'
import { array, date, number, object, boolean, string } from 'yup'
import { useEffect, useCallback, type ElementType } from 'react'
import { Modal } from 'components/Modal/Modal'
import { useSnackbar } from 'stores/useSnackbar'
import { colorPrimitives } from 'components/Theme'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { TextField } from 'components/Shared/TextField'
import type { components } from 'api/playerPayback/api'
import {
  Box,
  Button,
  InputAdornment,
  InputLabel,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material'
import { DateField, TimeField } from '@mui/x-date-pickers'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { format, formatISO, isValid, parse, parseISO } from 'date-fns'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { isPresent } from '@jjvgaming/player-payback-library'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { GrantPointsOnCheckInSwitch } from './GrantPointsOnCheckInSwitch'
import { daysOfWeek } from 'utils/util'
import { ScheduleRow } from 'pages/ClientManagement/LicensedEstablishments/LicensedEstablishmentNewPage/ScheduleRow'
import { type CheckInPointBonusSchedule } from 'types/api'
import { ActivityButton } from 'components/ActivityButton'
import { editBadgeRewardEndDateSchema } from 'utils/schemas/BadgeRewardSchema'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

interface GrantCheckInsForm {
  enablePatronCheckInPointBonus: boolean
  checkInsDailyMax: number
  enablePointMultiplier: boolean
  startDate?: Date | null
  startTime?: string | null
  endDate?: Date | null
  endTime?: string | null
  schedules?: CheckInPointBonusSchedule[]
}

export const GrantCheckInsSchema = object({
  enablePatronCheckInPointBonus: boolean().required(
    'Enable Patron Point Bonus is required.'
  ),
  checkInsDailyMax: number()
    .typeError('Max Check-ins value must be a number.')
    .test(
      'is-positive-or-zero',
      'Value must be a positive number or zero',
      (value) => {
        return value === 0 || (typeof value === 'number' && value > 0)
      }
    )
    .required('Max Check-ins value is required.'),
  enablePointMultiplier: boolean().required(
    'Enable Point Multiplier is required.'
  ),
  schedules: array().test({
    name: 'pointMultiplierScheduleRequired',
    message:
      'Enable at least one day with start and end times for the points multiplier',
    test: function (value) {
      return (
        !this.parent.enablePointMultiplier ||
        Boolean(
          value?.some((schedule) => schedule?.opening && schedule?.closing)
        )
      )
    },
  }),
  startDate: date()
    .nullable()
    .typeError('Must enter a valid start date')
    .transform((currentValue, originalValue, context) => {
      if (context.isType(currentValue)) {
        return currentValue
      }
      if (
        originalValue === '' ||
        originalValue === 'MM/DD/YYYY' ||
        originalValue === 'mm/dd/yyyy'
      ) {
        return null
      }
      return currentValue
    })
    .notRequired()
    .test({
      name: 'startDateRequiredWhenStartOrEndTimePresent',
      message: 'Start date is required when a start or end time is provided',
      test: function (value) {
        const { startTime, endDate, endTime } = this.parent
        const startDateIsSet = value instanceof Date && isValid(value)
        const endDateEntered = endDate instanceof Date && isValid(endDate)
        return (!startTime && !endDateEntered && !endTime) || startDateIsSet
      },
    }),
  startTime: string()
    .nullable()
    .notRequired()
    .matches(/^(0?[1-9]|1[0-2]):[0-5]\d (AM|PM)$/, {
      message: 'Must enter a valid start time.',
      excludeEmptyString: true,
    })
    .test({
      name: 'startTimeRequiredWhenStartOrEndDatePresent',
      message:
        'Start time is required when a start or end date/time is provided',
      test: function (value) {
        const { startDate, endDate, endTime } = this.parent
        const startDateEntered = startDate instanceof Date && isValid(startDate)
        const endDateEntered = endDate instanceof Date && isValid(endDate)
        return (
          (!startDateEntered && !endDateEntered && !endTime) || Boolean(value)
        )
      },
    }),
  endDate: editBadgeRewardEndDateSchema
    .notRequired()
    .test({
      name: 'endDateRequiredWhenEndTimePresent',
      message: 'End date is required when end time is provided',
      test: function (value) {
        const endTimeEntered = Boolean(this.parent.endTime)
        return !endTimeEntered || (value instanceof Date && isValid(value))
      },
    })
    .test({
      name: 'endTimeRequiredWhenEndDatePresent',
      message: 'End time is required when end date is provided',
      test: function (value) {
        const endDateEntered = value instanceof Date && isValid(value)
        return !endDateEntered || Boolean(this.parent.endTime)
      },
    }),
  endTime: string()
    .nullable()
    .notRequired()
    .matches(/^(0?[1-9]|1[0-2]):[0-5]\d (AM|PM)$/, {
      message: 'Must enter a valid end time.',
      excludeEmptyString: true,
    })
    .test({
      name: 'endDateRequiredWhenEndTimePresent',
      message: 'End date is required when end time is provided',
      test: function (value) {
        const endDate = this.parent.endDate
        const endDateEntered = endDate instanceof Date && isValid(endDate)
        return !value || endDateEntered
      },
    })
    .test({
      name: 'endTimeRequiredWhenEndDatePresent',
      message: 'End time is required when end date is provided',
      test: function (value) {
        const endDate = this.parent.endDate
        const endDateEntered = endDate instanceof Date && isValid(endDate)
        return !endDateEntered || Boolean(value)
      },
    })
    .test({
      name: 'endTimeAfterStartTime',
      message: 'End date/time should be after start date/time',
      test: function (value) {
        const { startDate, startTime, endDate } = this.parent
        return (
          !startDate ||
          !startTime ||
          !endDate ||
          !value ||
          parse(value, 'hh:mm a', endDate) >
            parse(startTime, 'hh:mm a', startDate)
        )
      },
    }),
})

export const SettingsModal = ({
  rewardsProgram,
  checkInPolicy,
  isModalVisible,
  toggleModalVisibility,
  daysToDisplay = [],
}: {
  rewardsProgram: components['schemas']['FullRewardsProgramDTO']
  checkInPolicy: components['schemas']['FullCheckInPolicyDTO']
  isModalVisible: boolean
  toggleModalVisibility: () => void
  daysToDisplay: CheckInPointBonusSchedule[]
}) => {
  const EventIconComponent = resolveIconComponent(EventIcon)
  const AccessTimeIconComponent = resolveIconComponent(AccessTimeIcon)
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const setMessage = useSnackbar((state) => state.setMessage)
  const queryClient = useQueryClient()
  const theme = useTheme()

  const formMethods = useForm<GrantCheckInsForm>({
    resolver: yupResolver(GrantCheckInsSchema),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    reset,
  } = formMethods

  const resetForm = useCallback(() => {
    reset({
      enablePatronCheckInPointBonus: Boolean(
        checkInPolicy?.enablePatronCheckInPointBonus
      ),
      checkInsDailyMax: Number(checkInPolicy?.checkInsDailyMax),
      enablePointMultiplier:
        Boolean(checkInPolicy.startDate) ||
        Boolean(checkInPolicy.endDate) ||
        Boolean(
          checkInPolicy?.checkInPointBonusSchedules?.some(
            (policy) => policy.patronCheckInPointBonusMultiplier !== undefined
          )
        ),
      startDate: checkInPolicy.startDate
        ? parseISO(checkInPolicy.startDate)
        : null,
      startTime: checkInPolicy.startDate
        ? format(parseISO(checkInPolicy.startDate), 'hh:mm a')
        : '',
      endDate: checkInPolicy.endDate ? parseISO(checkInPolicy.endDate) : null,
      endTime: checkInPolicy.endDate
        ? format(parseISO(checkInPolicy.endDate), 'hh:mm a')
        : '',
    })
  }, [checkInPolicy])

  useEffect(() => {
    resetForm()
  }, [resetForm])

  const enablePatronPointBonus = watch('enablePatronCheckInPointBonus')
  const enablePointMultiplier = watch('enablePointMultiplier')

  const closeModal = () => {
    toggleModalVisibility()
    resetForm()
  }

  const putCheckInPolicy = useTypedApiClient({
    path: '/admin/policies/checkins/{id}',
    method: 'put',
  })

  const updateCheckInPolicyMutation = useMutation({
    mutationFn: async ({
      checkInsDailyMax,
      enablePatronCheckInPointBonus,
      enablePointMultiplier,
      schedules,
      startDate,
      startTime,
      endDate,
      endTime,
    }: GrantCheckInsForm) => {
      const checkInPointBonusSchedules: CheckInPointBonusSchedule[] = []
      schedules?.forEach((schedule) => {
        if (schedule.opening && schedule.closing && enablePointMultiplier) {
          checkInPointBonusSchedules.push({
            ...schedule,
            patronCheckInPointBonusMultiplier: 2,
          })
        }
      })

      if (checkInPolicy?.id && checkInPolicy?.rewardsProgramId) {
        checkInPolicy.enablePatronCheckInPointBonus =
          enablePatronCheckInPointBonus
        checkInPolicy.checkInPointBonusSchedules = checkInPointBonusSchedules
        checkInPolicy.checkInsDailyMax = checkInsDailyMax
        checkInPolicy.startDate =
          enablePointMultiplier && startDate && startTime
            ? formatISO(parse(startTime, 'hh:mm a', startDate))
            : null
        checkInPolicy.endDate =
          enablePointMultiplier && endDate && endTime
            ? formatISO(parse(endTime, 'hh:mm a', endDate))
            : null
        checkInPolicy.patronCheckInPointBonusMin =
          checkInPolicy.patronCheckInPointBonusMin ?? 1
        checkInPolicy.patronCheckInPointBonusMax =
          checkInPolicy.patronCheckInPointBonusMax ?? 1
        return await putCheckInPolicy(checkInPolicy)
      }
    },
  })

  useEffect(() => {
    if (updateCheckInPolicyMutation.isSuccess) {
      queryClient
        .invalidateQueries({
          queryKey: [
            '/admin/policies/checkins/rewards-programs',
            rewardsProgram.id,
          ],
        })
        .finally(() => {})
      setMessage('Check In Policy has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      closeModal()
    } else {
      const error: any = updateCheckInPolicyMutation.error as Error
      if (error?.message) {
        setMessage(error?.message, 'error', {
          vertical: 'top',
          horizontal: 'right',
        })
      }
    }
  }, [
    updateCheckInPolicyMutation.isSuccess,
    updateCheckInPolicyMutation.isError,
  ])

  const updateCheckInPolicy = handleSubmit((data) => {
    updateCheckInPolicyMutation.mutate(data)
  })

  return (
    <Modal
      isOpen={isModalVisible}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '70%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={updateCheckInPolicy}>
          <ModalFullHeader
            title="Grant Check-Ins"
            subTitle={rewardsProgram.name}
            toggleIsOpen={closeModal}
          />
          <ModalFullBody heightModal={heightModal}>
            <Stack spacing={5}>
              <GrantPointsOnCheckInSwitch />
              {enablePatronPointBonus && (
                <Box>
                  <Box className="flex flex-col sm:flex-row justify-stretch w-full px-1">
                    <Box className="w-full sm:w-1/2 sm:pt-4 flex">
                      <Typography>
                        Max Number of Check-ins per 24 Hour Period
                      </Typography>
                      <Typography color={colorPrimitives.redGaming}>
                        *
                      </Typography>
                    </Box>
                    <Box className="flex flex-row gap-6 w-full sm:w-1/2">
                      <TextField
                        placeholder="checkInsDailyMax"
                        {...register('checkInsDailyMax', { required: true })}
                        error={isPresent(errors.checkInsDailyMax)}
                        sx={{ width: '100px' }}
                        bottomContent={
                          errors.checkInsDailyMax?.message && (
                            <FormFieldErrorMessage
                              message={errors.checkInsDailyMax.message}
                            />
                          )
                        }
                      />
                      <Typography className="sm:pt-4">Check-ins</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {/* Points Multiplier Section */}
              {enablePatronPointBonus && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: theme.palette.grey[300],
                    m: '16px',
                    gap: '16px',
                    p: '16px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '4px',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="h4">Points Multiplier</Typography>
                    <Typography variant="body-3">
                      When enabled the user will receive 2x points per check in.
                    </Typography>
                  </Box>
                  <Controller
                    name="enablePointMultiplier"
                    render={({ field: { onChange, value } }) => {
                      return <Switch checked={value} onChange={onChange} />
                    }}
                  />
                </Box>
              )}
              {enablePatronPointBonus && enablePointMultiplier && (
                <Box>
                  <Stack spacing={3} mb={3} sx={{ maxWidth: '520px' }}>
                    <Box>
                      <InputLabel htmlFor="startDate">
                        Start Date/Time
                      </InputLabel>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Controller
                            name="startDate"
                            render={({ field }) => (
                              <DateField
                                {...field}
                                id="startDate"
                                value={field.value ?? null}
                                fullWidth
                                slotProps={{
                                  textField: {
                                    error: isPresent(errors.startDate),
                                    InputProps: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <EventIconComponent />
                                        </InputAdornment>
                                      ),
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                          {errors.startDate?.message && (
                            <FormFieldErrorMessage
                              message={errors.startDate.message}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Controller
                            name="startTime"
                            render={({ field }) => (
                              <TimeField
                                id="startTime"
                                name={field.name}
                                inputRef={field.ref}
                                onBlur={field.onBlur}
                                value={
                                  field.value
                                    ? parse(field.value, 'hh:mm a', new Date())
                                    : null
                                }
                                onChange={(value) =>
                                  field.onChange(
                                    value
                                      ? isValid(value)
                                        ? format(value, 'hh:mm a')
                                        : 'Invalid Date'
                                      : ''
                                  )
                                }
                                fullWidth
                                slotProps={{
                                  textField: {
                                    error: isPresent(errors.startTime),
                                    InputProps: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <AccessTimeIconComponent />
                                        </InputAdornment>
                                      ),
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                          {errors.startTime?.message && (
                            <FormFieldErrorMessage
                              message={errors.startTime.message}
                            />
                          )}
                        </Box>
                      </Stack>
                    </Box>
                    <Box>
                      <InputLabel htmlFor="endDate">End Date/Time</InputLabel>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Controller
                            name="endDate"
                            render={({ field }) => (
                              <DateField
                                {...field}
                                id="endDate"
                                value={field.value ?? null}
                                fullWidth
                                slotProps={{
                                  textField: {
                                    error: isPresent(errors.endDate),
                                    InputProps: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <EventIconComponent />
                                        </InputAdornment>
                                      ),
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                          {errors.endDate?.message && (
                            <FormFieldErrorMessage
                              message={errors.endDate.message}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Controller
                            name="endTime"
                            render={({ field }) => (
                              <TimeField
                                id="endTime"
                                name={field.name}
                                inputRef={field.ref}
                                onBlur={field.onBlur}
                                value={
                                  field.value
                                    ? parse(field.value, 'hh:mm a', new Date())
                                    : null
                                }
                                onChange={(value) =>
                                  field.onChange(
                                    value
                                      ? isValid(value)
                                        ? format(value, 'hh:mm a')
                                        : 'Invalid Date'
                                      : ''
                                  )
                                }
                                fullWidth
                                slotProps={{
                                  textField: {
                                    error: isPresent(errors.endTime),
                                    InputProps: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <AccessTimeIconComponent />
                                        </InputAdornment>
                                      ),
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                          {errors.endTime?.message && (
                            <FormFieldErrorMessage
                              message={errors.endTime.message}
                            />
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                  <Typography variant="subtitle-1" component={'p'}>
                    Point Multiplier Schedule
                  </Typography>
                  {errors.schedules?.message && (
                    <FormFieldErrorMessage message={errors.schedules.message} />
                  )}
                  <Typography variant="body-3">
                    {`Select the days you'd like to award 2x points`}
                  </Typography>
                  <Stack spacing={2} mt="20px">
                    {daysOfWeek.map((day, index) => {
                      return (
                        <Box
                          key={day}
                          style={{ height: '85px' }}
                          height={'85'}
                          className="flex flex-col justify-center"
                        >
                          <ScheduleRow
                            day={day}
                            index={index}
                            key={day}
                            defaultValues={{
                              opening: daysToDisplay?.[index]?.opening,
                              closing: daysToDisplay?.[index]?.closing,
                              patronCheckInPointBonusMultiplier:
                                daysToDisplay?.[index]
                                  ?.patronCheckInPointBonusMultiplier ?? 0,
                            }}
                            openLabel="Enabled"
                            closedLabel="Disabled"
                          />
                        </Box>
                      )
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={closeModal}>
              Cancel
            </Button>
            <ActivityButton
              active={updateCheckInPolicyMutation.isPending}
              variant="contained"
              type="submit"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}
